/**
 * Duration values passed from the landing page form to Motor Journeys.
 *
 * The landing page form is a plain GET form, so a `duration` query param only
 * reaches Motor Journeys if a control named `duration` is serialised on submit.
 * Motor Journeys parses that param as a single space-separated `"<n> <unit>"`
 * string (see motor-journeys `src/actions/journey/index.ts` →
 * `extractDurationFromParam`) and validates it against
 * `hours <= 12`, `days <= 28`, `weeks <= 4` with a lowercase, pluralisable unit
 * (motor-journeys `src/types/common.ts`). Anything else is silently discarded
 * downstream and the journey falls back to its own default, so every value we
 * emit has to come from this list.
 *
 * IMPORTANT: an *absent* `duration` param is meaningful, not a gap to fill.
 * Motor Journeys owns the default cover duration, and only applies it when no
 * duration param is present. A landing page should override that only when its
 * own name promises something specific ("one day car insurance"); otherwise it
 * must stay silent and let the journey decide. Never substitute a fallback
 * duration — a page that passes one pins its visitors to whatever value was
 * hardcoded here, so any later change to the journey default (or a new
 * experiment on it) would silently skip that page's traffic.
 */

// Kept as the shared vocabulary for the quick-pick row in LandingPageForm, which
// deliberately offers only these three, while DURATION_OPTIONS below covers
// everything a page can be configured with.
export const FIXED_DURATION_ONE_DAY = '1 day';
export const FIXED_DURATION_TWO_DAYS = '2 days';
export const FIXED_DURATION_ONE_WEEK = '1 week';

const pluralise = (n: number, unit: string) => `${n} ${unit}${n > 1 ? 's' : ''}`;

// Ordered so the Builder enum reads hours → days → weeks. Bounds mirror
// motor-journeys' durationSchema; widening them here would produce values it
// silently rejects.
export const DURATION_OPTIONS: readonly string[] = [
  ...Array.from({ length: 12 }, (_, i) => pluralise(i + 1, 'hour')),
  ...Array.from({ length: 28 }, (_, i) => pluralise(i + 1, 'day')),
  ...Array.from({ length: 4 }, (_, i) => pluralise(i + 1, 'week')),
];

/**
 * Narrows a Builder-configured duration to something Motor Journeys accepts.
 *
 * Returns `undefined` for an unset, empty or unrecognised value so the caller
 * omits the param rather than guessing. This is what satisfies TCEX-3196's
 * unhappy path: an unmappable duration falls back to the journey's current
 * default instead of erroring or sending a bad value.
 */
export const resolveDurationParam = (configured?: string): string | undefined => {
  const trimmed = configured?.trim();
  return trimmed && DURATION_OPTIONS.includes(trimmed) ? trimmed : undefined;
};
